import { useCallback, useEffect, useRef, useState } from "react";
import type { LatLng, MapBounds, SettledMapView } from "../../utils/map-viewport";
import { estimateBoundsFromCenterZoom } from "../../utils/map-viewport";

type MapStateLike = {
  center: LatLng;
  zoom: number;
};

type BoundsChangedArgs = {
  center: LatLng;
  zoom: number;
  bounds: MapBounds;
};

/**
 * Live camera updates locally (smooth pan/zoom). Marker viewport + parent
 * mapState refresh only after movement settles — avoids layout re-renders and
 * marker/overlay rebuilds on every wheel tick.
 *
 * Zoom-out settles faster so pins reappear in the wider viewport promptly.
 */
export function useSettledMapView(
  mapState: MapStateLike,
  setMapState: React.Dispatch<React.SetStateAction<MapStateLike>>,
  settleMs = 220
) {
  const [view, setView] = useState<MapStateLike>({
    center: mapState.center,
    zoom: mapState.zoom,
  });
  const [settled, setSettled] = useState<SettledMapView>({
    center: mapState.center,
    zoom: mapState.zoom,
    bounds: estimateBoundsFromCenterZoom(mapState.center, mapState.zoom),
  });
  const [isMoving, setIsMoving] = useState(false);

  const pendingRef = useRef<BoundsChangedArgs | null>(null);
  const rafRef = useRef<number | null>(null);
  const settleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const programmaticRef = useRef(false);
  const programmaticTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const movingRef = useRef(false);
  const liveZoomRef = useRef(mapState.zoom);
  const mapStateRef = useRef(mapState);
  mapStateRef.current = mapState;
  const lastProgrammaticKey = useRef(
    `${mapState.center[0]},${mapState.center[1]},${mapState.zoom}`
  );

  const applySettled = useCallback(
    (center: LatLng, zoom: number, bounds: MapBounds | null) => {
      const nextBounds =
        bounds || estimateBoundsFromCenterZoom(center, zoom);
      setSettled({
        center,
        zoom,
        bounds: nextBounds,
      });
      lastProgrammaticKey.current = `${center[0]},${center[1]},${zoom}`;
      setMapState({ center, zoom });
      movingRef.current = false;
      setIsMoving(false);
    },
    [setMapState]
  );

  const flushPendingSettle = useCallback(() => {
    const pending = pendingRef.current;
    if (pending) {
      applySettled(pending.center, pending.zoom, pending.bounds);
      return;
    }
    // No bounds event yet (common after programmatic jumps) — estimate so
    // markers remount (otherwise /highlights/… showed radius but no pins).
    const { center, zoom } = mapStateRef.current;
    applySettled(center, zoom, null);
  }, [applySettled]);

  // Parent/programmatic camera changes (selected place, country click, etc.)
  useEffect(() => {
    const key = `${mapState.center[0]},${mapState.center[1]},${mapState.zoom}`;
    if (key === lastProgrammaticKey.current) return;
    lastProgrammaticKey.current = key;
    programmaticRef.current = true;
    liveZoomRef.current = mapState.zoom;
    // Drop stale pending + bounds so we don't flush a previous Europe viewport
    pendingRef.current = null;
    setView({ center: mapState.center, zoom: mapState.zoom });
    setSettled({
      center: mapState.center,
      zoom: mapState.zoom,
      // Temporary estimate so highlight pins aren't blank for 280ms+
      bounds: estimateBoundsFromCenterZoom(mapState.center, mapState.zoom),
    });
    setIsMoving(false);
    movingRef.current = false;
    if (settleRef.current) {
      clearTimeout(settleRef.current);
      settleRef.current = null;
    }
    if (programmaticTimerRef.current) {
      clearTimeout(programmaticTimerRef.current);
    }
    programmaticTimerRef.current = setTimeout(() => {
      programmaticRef.current = false;
      programmaticTimerRef.current = null;
      flushPendingSettle();
    }, 280);
    return () => {
      if (programmaticTimerRef.current) {
        clearTimeout(programmaticTimerRef.current);
        programmaticTimerRef.current = null;
      }
    };
  }, [mapState.center, mapState.zoom, flushPendingSettle]);

  useEffect(() => {
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      if (settleRef.current) clearTimeout(settleRef.current);
      if (programmaticTimerRef.current) {
        clearTimeout(programmaticTimerRef.current);
      }
    };
  }, []);

  const onBoundsChanged = useCallback(
    ({ center, zoom, bounds }: BoundsChangedArgs) => {
      const prevZoom = liveZoomRef.current;
      pendingRef.current = { center, zoom, bounds };
      liveZoomRef.current = zoom;

      if (rafRef.current == null) {
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          const pending = pendingRef.current;
          if (!pending) return;
          setView({ center: pending.center, zoom: pending.zoom });
        });
      }

      // Programmatic pans: live view only — settle once when the hold ends
      if (programmaticRef.current) {
        return;
      }

      // Zooming out: refresh marker viewport quickly so pins reappear in the
      // wider area (stale tight bounds were leaving a single pin on screen).
      const zoomingOut = zoom < prevZoom - 0.2;
      const delay = zoomingOut ? 70 : settleMs;

      if (!movingRef.current) {
        movingRef.current = true;
        setIsMoving(true);
      }
      if (settleRef.current) clearTimeout(settleRef.current);
      settleRef.current = setTimeout(() => {
        settleRef.current = null;
        flushPendingSettle();
      }, delay);
    },
    [settleMs, flushPendingSettle]
  );

  return { view, settled, isMoving, onBoundsChanged, liveZoomRef };
}
